import React, {
  type FormEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../utils';

import './FormLayout.css';

/** Provides a consistent visual container for auth pages */
function FormLayout({
  children,
  footerContent,
  title,
  subtitle,
  errorContent,
  versionMessage,
  onSubmit: handleSubmit = () => {},
}: {
  children: React.ReactNode;
  footerContent?: React.ReactNode;
  title: string;
  subtitle?: string;
  errorContent?: React.ReactNode;
  versionMessage?: string;
  onSubmit?: FormEventHandler<HTMLFormElement>;
}): React.ReactNode {
  const l = useInvertedLocalize();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [brandImgIndex, setBrandImgIndex] = useState(0);
  const [isBrandImgLoading, setIsBrandImgLoading] = useState(true);
  const brandingClassName = cx(
    'Auth_FormLayout_branding',
    isBrandImgLoading && 'Auth_FormLayout_branding--loading'
  );
  const logoSrc = `${
    process.env.REACT_APP_AUTH_API ?? ''
  }/ECMv6/api/idw/resource/image/app/SaviyntR_White-Original.png`;

  const handleBrandImgLoad = () => {
    setIsBrandImgLoading(false);
  };

  useEffect(() => {
    if (isBrandImgLoading || timeoutRef.current !== null) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setIsBrandImgLoading(true);
      setBrandImgIndex((prev) => prev + 1);
      timeoutRef.current = null;
    }, 5000);
  }, [isBrandImgLoading]);

  return (
    <main className='Auth_FormLayout'>
      <form onSubmit={handleSubmit} className='Auth_FormLayout_container'>
        <Box className='Auth_FormLayout_form'>
          <Box className='Auth_FormLayout_section'>
            <img
              src={logoSrc}
              alt={l('Saviynt logo')}
              className='Auth_FormLayout_logo'
            />
            <Typography kind='h3' className='Auth_FormLayout_title'>
              {title}
            </Typography>
            {subtitle && (
              <Typography kind='body2' className='Auth_FormLayout_subtitle'>
                {subtitle}
              </Typography>
            )}
          </Box>

          <Box className='Auth_FormLayout_section'>{children}</Box>

          {(errorContent || footerContent) && (
            <Box className='Auth_FormLayout_section Auth_FormLayout_section--isFooter'>
              {errorContent}
              {footerContent}
            </Box>
          )}
        </Box>
        {versionMessage && (
          <Typography kind='body3' className='Auth_FormLayout_version'>
            {versionMessage}
          </Typography>
        )}
      </form>
      <section className={brandingClassName}>
        {brandImgIndex > 0 && (
          <img
            alt=''
            src={getBrandImg(brandImgIndex - 1)}
            className='Auth_FormLayout_brandImg Auth_FormLayout_brandImg--previous'
            key={brandImgIndex - 1}
          />
        )}
        <img
          alt=''
          src={getBrandImg(brandImgIndex)}
          className='Auth_FormLayout_brandImg'
          key={brandImgIndex}
          onLoad={handleBrandImgLoad}
        />
      </section>
    </main>
  );
}

function getBrandImg(index: number) {
  return `${
    process.env.REACT_APP_AUTH_API ?? ''
  }/ECMv6/api/idw/resource/image/app/bg${(index % 9) + 1}.jpg`;
}

export default FormLayout;
